import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useStorageConditions } from '@/hooks/useStorage'
import type { StorageCondition, TemperatureFamily } from '@/types/storage'

/**
 * Validated storage conditions (design note §5.2) in the UI.
 *
 * Colour follows the TEMPERATURE FAMILY, not the individual condition, because
 * the family is what tells you at a glance how cold something is — and the two
 * cryogenic conditions genuinely belong together even though they are not
 * interchangeable.
 *
 * Labels and temperatures come from the server rather than a local table: they
 * describe the same enum put-away validates on, and a second copy here would
 * eventually disagree with it.
 */

export const FAMILY_TONE: Record<TemperatureFamily, string> = {
  CRYOGENIC: 'border-purple-300 bg-purple-50 text-purple-800',
  ULTRA_LOW: 'border-sky-300 bg-sky-50 text-sky-800',
  FREEZER: 'border-blue-300 bg-blue-50 text-blue-800',
  REFRIGERATED: 'border-teal-300 bg-teal-50 text-teal-800',
  AMBIENT_CRT: 'border-amber-300 bg-amber-50 text-amber-800',
}

/**
 * Reads a condition enum value into something a lab tech can read.
 *
 * The inventory Storage Profile stores the condition as the raw enum value —
 * put-away validation compares it against a database enum, so it cannot be a
 * label — and this is what turns that back into words wherever the generic
 * profile-field widgets render it.
 */
export function useConditionOptionLabel(): (option: string) => string {
  const reference = useStorageConditions()
  return (option) => {
    const meta = reference.data?.conditions.find((c) => c.name === option)
    // The name already carries its temperature ("Freezer -20 C"); a switched-off
    // one is still named, so a profile on it reads as what it is.
    if (!meta) return option
    return meta.active ? meta.label : `${meta.label} (off)`
  }
}

export function ConditionBadge({
  condition,
  showTemperature = true,
}: {
  condition: StorageCondition | null
  showTemperature?: boolean
}) {
  const reference = useStorageConditions()
  const meta = reference.data?.conditions.find((c) => c.name === condition)

  if (!condition) {
    // Not "unknown" — an unset condition is a real state with a consequence:
    // nothing that states a requirement can be put away here.
    return <span className='text-xs text-muted-foreground'>Not set</span>
  }

  return (
    <Badge
      variant='outline'
      className={cn('font-normal', meta?.family ? FAMILY_TONE[meta.family] : undefined)}
      title={meta?.usedFor}
    >
      {meta?.label ?? condition}
      {/* Skipped when the name already says it ("Freezer -20 C"), so it is not shown twice. */}
      {showTemperature && meta?.temperature && !/\d/.test(meta.label) && (
        <span className='ml-1.5 opacity-70'>{meta.temperature}</span>
      )}
    </Badge>
  )
}
